import type * as React from 'react';
import { getPartitionedNativeProps, slot } from '@fluentui/react-components';
import type { ChatInputEntityProps, ChatInputEntityState } from './ChatInputEntity.types';

// If you add JSX to this file, be sure to change the file type to .tsx

/**
 * Create the state required to render ChatInputEntity.
 *
 * The returned state can be modified with hooks such as useChatInputEntityStyles_unstable,
 * before being passed to renderChatInputEntity_unstable.
 *
 * @param props - props from this instance of ChatInputEntity
 * @param ref - reference to root HTMLElement of ChatInputEntity
 */
export const useChatInputEntity_unstable = (
  props: ChatInputEntityProps,
  ref: React.Ref<HTMLSpanElement | HTMLAnchorElement>,
): ChatInputEntityState => {
  const { as } = props;

  const elementType = as ?? 'span';

  const nativeProps = getPartitionedNativeProps({ props, primarySlotTagName: elementType });

  const defaultTextSlotProps =
    elementType === 'a'
      ? {
          target: '_blank',
          rel: 'noopener',
          // We don't want to enable tab focus for components inside the ChatInput
          tabIndex: -1,
          ...nativeProps.primary,
          as: 'a' as const,
          // This cast is safe to do at this point because we know the type of element we are rendering is an anchor
          ref: ref as React.Ref<HTMLAnchorElement>,
        }
      : { ...nativeProps.primary, as: 'span' as const, ref };

  return {
    components: {
      root: 'span',
      sensitivity: 'span',
      text: elementType,
    },
    root: slot.always(props.root, {
      defaultProps: nativeProps.root,
      elementType: 'span',
    }),
    sensitivity: slot.optional(props.sensitivity, { elementType: 'span' }),
    text: slot.optional(props.text, {
      defaultProps: defaultTextSlotProps,
      renderByDefault: true,
      elementType,
    }),
  };
};
